// ABOUTME: Right-anchored drawer listing the user's notifications newest-first as List rows
// ABOUTME: (type icon in an Avatar, divider between rows). Unread-only by default with a
// ABOUTME: "Show all" toggle; clicking a row marks it read and opens its deep link in a new tab.

import {
    ArrowForwardIcon,
    Avatar,
    Box,
    Button,
    Drawer,
    FormControlLabel,
    InfoOutlinedIcon,
    List,
    ListItem,
    Switch,
    Typography,
} from '@ds/coolshop';
// Allowlisted direct MUI import: the DS has no LinearProgress equivalent (see CLAUDE.md).
import LinearProgress from '@mui/material/LinearProgress';
import { ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NotificationsQuery } from '../graphql/notifications.generated';

type NotificationItem = NotificationsQuery['notifications'][number];

// Maps a notification type to its row icon (rendered inside the Avatar, which provides the
// colored background); extend when new NotificationType values are added.
const TYPE_ICONS: Record<string, ReactNode> = {
    info: <InfoOutlinedIcon fontSize="small" />,
};

type NotificationPanelProps = {
    open: boolean;
    onClose: () => void;
    notifications: NotificationItem[];
    unreadCount: number;
    loading: boolean;
    showAll: boolean;
    onToggleShowAll: (showAll: boolean) => void;
    onMarkRead: (id: string) => void;
    onMarkAllRead: () => void;
    onRefetch: () => void;
};

export function NotificationPanel({
    open,
    onClose,
    notifications,
    unreadCount,
    loading,
    showAll,
    onToggleShowAll,
    onMarkRead,
    onMarkAllRead,
    onRefetch,
}: NotificationPanelProps) {
    const intl = useIntl();

    // Catch-up on open so nothing is missed while the panel was closed / the socket dropped.
    useEffect(() => {
        if (open) onRefetch();
    }, [open, onRefetch]);

    const handleNotificationClick = (notification: NotificationItem) => {
        if (!notification.read) {
            onMarkRead(notification.id);
        }
        if (notification.deepLink) {
            window.open(notification.deepLink, '_blank', 'noopener');
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h5" color="primary">
                    <FormattedMessage id="notifications.title" defaultMessage="Notifications" />
                </Typography>
                <Button variant="text" onClick={onMarkAllRead} disabled={unreadCount === 0}>
                    {intl.formatMessage({
                        id: 'notifications.markAllRead',
                        defaultMessage: 'Mark all as read',
                    })}
                </Button>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showAll}
                            onChange={(_, checked) => onToggleShowAll(checked)}
                            size="small"
                        />
                    }
                    label={intl.formatMessage({
                        id: 'notifications.showAll',
                        defaultMessage: 'Show all',
                    })}
                />
            </Box>
            {/* Thin progress bar while a (re)fetch is in flight with content already visible. */}
            <Box minHeight={4}>{loading && <LinearProgress />}</Box>
            <Box flexGrow={1} overflow="auto">
                {notifications.length === 0 && !loading && (
                    <Typography color="textSecondary" textAlign="center">
                        {showAll ? (
                            <FormattedMessage
                                id="notifications.empty"
                                defaultMessage="You have no notifications."
                            />
                        ) : (
                            <FormattedMessage
                                id="notifications.emptyUnread"
                                defaultMessage="You're all caught up - No unread notifications."
                            />
                        )}
                    </Typography>
                )}
                {notifications.length > 0 && (
                    <List fullwidth>
                        {notifications.map(notification => (
                            <ListItem
                                key={notification.id}
                                divider
                                cursor={
                                    notification.deepLink || !notification.read
                                        ? 'pointer'
                                        : 'default'
                                }
                                fullWidth
                                onClick={() => handleNotificationClick(notification)}
                                startAdornment={
                                    <Avatar color={notification.read ? 'grey' : 'info'} size="sm">
                                        {TYPE_ICONS[notification.type ?? 'info']}
                                    </Avatar>
                                }
                                endAdornment={
                                    notification.deepLink ? (
                                        <ArrowForwardIcon color="action" fontSize="small" />
                                    ) : undefined
                                }
                            >
                                <Box minWidth={0} opacity={notification.read ? 0.6 : 1} px={1}>
                                    <Typography variant="body1">
                                        {notification.read ? (
                                            notification.title
                                        ) : (
                                            <strong>{notification.title}</strong>
                                        )}
                                    </Typography>
                                    {notification.message && (
                                        <Typography variant="body2" color="textSecondary">
                                            {notification.message}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="textSecondary">
                                        {intl.formatDate(new Date(notification.createdAt), {
                                            dateStyle: 'medium',
                                            timeStyle: 'short',
                                        })}
                                    </Typography>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Drawer>
    );
}
